import { Article } from '../types';
import { decodeHTMLEntities, fixEncoding, fixPolishText } from './encoding';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

// Articles published within the last 24h relative to *now*. Replaces the
// old midnight-cliff "today only" rule, which made a 11pm article vanish
// at 12:01am even if the user was mid-read.
const isWithinLast24h = (date: Date): boolean => {
  const diff = Date.now() - date.getTime();
  return diff >= 0 && diff < TWENTY_FOUR_HOURS_MS;
};

export const parseRSSXML = (xmlText: string, feedName: string, limit: number = 20): Article[] => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'application/xml');

  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    const errorText = parseError.textContent || 'Unknown XML error';
    throw new Error(`XML parsing failed: ${errorText.substring(0, 100)}`);
  }

  const items: Article[] = [];
  let itemElements = xmlDoc.querySelectorAll('item');

  if (itemElements.length === 0) {
    itemElements = xmlDoc.querySelectorAll('entry');
  }

  // Limit articles per source based on user settings
  const limitedItems = Array.from(itemElements).slice(0, limit);

  limitedItems.forEach((item) => {
    let title = item.querySelector('title')?.textContent || 'Untitled';
    let link = item.querySelector('link')?.textContent || item.querySelector('link')?.getAttribute('href') || '#';

    let description = item.querySelector('description')?.textContent ||
      item.querySelector('summary')?.textContent ||
      item.querySelector('content')?.textContent ||
      item.querySelector('content\\:encoded')?.textContent || '';

    const pubDateStr = item.querySelector('pubDate')?.textContent ||
      item.querySelector('published')?.textContent ||
      item.querySelector('updated')?.textContent ||
      new Date().toISOString();

    if (link === '#') {
      const linkEl = item.querySelector('link[href]');
      if (linkEl) link = linkEl.getAttribute('href') || '#';
    }

    // Decode HTML entities and fix UTF-8 mojibake. One pass is enough now
    // that the mojibake map is restricted to multi-char sequences — the
    // old double-pass was a hedge against destructive single-char fixes
    // that no longer exist.
    title = fixPolishText(title);
    title = decodeHTMLEntities(title);
    title = fixEncoding(title);

    description = fixPolishText(description);
    description = decodeHTMLEntities(description);
    description = fixEncoding(description);

    const cleanedDescription = description
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
      .replace(/<img[^>]*>/gi, '')
      .replace(/<a[^>]*>/gi, '')
      .replace(/<\/a>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#(\d+);/g, (_match, dec) => String.fromCharCode(dec))
      .replace(/&#x([0-9a-f]+);/gi, (_match, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/&apos;/g, "'")
      .replace(/&Oacute;/g, 'Ó')
      .replace(/&oacute;/g, 'ó')
      .replace(/&Aacute;/g, 'Á')
      .replace(/&aacute;/g, 'á')
      .replace(/&Eacute;/g, 'É')
      .replace(/&eacute;/g, 'é')
      .replace(/&Iacute;/g, 'Í')
      .replace(/&iacute;/g, 'í')
      .replace(/&Uacute;/g, 'Ú')
      .replace(/&uacute;/g, 'ú')
      .replace(/&Ntilde;/g, 'Ñ')
      .replace(/&ntilde;/g, 'ñ')
      .trim();

    const articleDate = new Date(pubDateStr);

    // Check if description is empty or useless
    const hasEmptyDescription = !cleanedDescription ||
      cleanedDescription.toLowerCase() === 'comments' ||
      cleanedDescription.toLowerCase() === 'comment';

    // Only include articles from the last 24h (rolling window).
    if (isWithinLast24h(articleDate)) {
      const trimmedLink = link.trim();
      items.push({
        id: `${feedName}-${trimmedLink}`,
        title: title.trim(),
        link: trimmedLink,
        description: cleanedDescription,
        pubDate: articleDate,
        feedName: feedName,
        hasEmptyDescription,
      });
    }
  });

  return items;
};
